// Component: AdminRecoveryPanel.jsx
// Purpose: 104 Element R (D67/R12.3) — the ADMIN BACKUP recovery surface. Per D67
//          ("a nice-to-have for the admin"), this is explicitly secondary to
//          StuckOrdersPanel.jsx (the user-facing primary path) — it exists so an
//          operator can act on the identifiers already surfaced by the D51 alert
//          payload (`detail.order_id`/`detail.instrument`, 02-architecture.md
//          §2.13 point 3) without a separate admin listing endpoint (none exists —
//          same gap noted in StuckOrdersPanel.jsx's ESCALATE comment). Reuses the
//          SAME RecoveryActionModal ack/note UI as the user-facing panel — no
//          forked confirmation flow for the same backend gate.

import React, { useState } from 'react';
import { PageHeader, Card, Input, Button } from '../../../components/ds';
import RecoveryActionModal from '../../../components/trading/RecoveryActionModal';
import { services } from '../../../services/api';
import { useSnackbar } from '../../../contexts/SnackbarContext';

const AdminRecoveryPanel = () => {
  /*>>> State <<<*/
  //! Admin-entered identifiers — from the alert payload / operator investigation,
  //! never a listing fetch (no such endpoint is registered — see the ESCALATE note
  //! in StuckOrdersPanel.jsx).
  const [sii, setSii] = useState('');
  const [lifecycleId, setLifecycleId] = useState('');
  const [instrumentId, setInstrumentId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [pendingMode, setPendingMode] = useState(null); // 'force-close' | 'repair-netting' | null
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState(null);
  const { showSnackbar } = useSnackbar();

  const canForceClose = sii.trim() && lifecycleId.trim();
  const canRepair = sii.trim() && instrumentId.trim() && accountId.trim();

  /*>>> Handlers <<<*/
  const closeModal = () => {
    setPendingMode(null);
    setActionError(null);
  };

  const confirm = async (form) => {
    setSubmitting(true);
    setActionError(null);
    try {
      if (pendingMode === 'force-close') {
        await services.selfServiceRecovery.adminForceClose(sii.trim(), {
          lifecycle_id: lifecycleId.trim(),
          reason: form.reason || 'admin_backup_force_close',
          compliance_note: form.compliance_note,
          acknowledge_loss: true,
        });
      } else {
        await services.selfServiceRecovery.adminRepairNetting(sii.trim(), {
          instrument_id: instrumentId.trim(),
          account_id: accountId.trim(),
          idempotency_key: `admin:${sii.trim()}:${instrumentId.trim()}:${Date.now()}`,
          compliance_note: form.compliance_note,
          true_exit_price: form.true_exit_price,
          acknowledge_loss: true,
        });
      }
      showSnackbar('Recovery action submitted.', 'success');
      closeModal();
    } catch (err) {
      const msg =
        err?.response?.data?.error?.message ||
        err?.response?.data?.detail ||
        'Could not complete this action. Please try again.';
      setActionError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  /*>>> Render <<<*/
  return (
    <div className="px-4 md:px-6 pb-8">
      <PageHeader
        title="Recovery"
        accent="(Admin backup)"
        subtitle="Backup channel only — the primary path is the user's own Stuck Orders screen (D67). Use the SII/identifiers from the alert payload."
      />

      <Card className="p-4 flex flex-col gap-3 max-w-lg">
        <label className="text-sm">
          Strategy instance ID (SII)
          <Input value={sii} onChange={(e) => setSii(e.target.value)} placeholder="e.g. 12:NIFTY-...:acct" />
        </label>

        <div className="flex flex-col gap-2">
          <span className="text-sm font-semibold">Force-close</span>
          <label className="text-sm">
            Lifecycle ID
            <Input value={lifecycleId} onChange={(e) => setLifecycleId(e.target.value)} placeholder="uuid" />
          </label>
          <Button
            variant="danger"
            size="sm"
            disabled={!canForceClose}
            onClick={() => { setActionError(null); setPendingMode('force-close'); }}
          >
            Force-close
          </Button>
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-sm font-semibold">Repair netting (manual: namespace only)</span>
          <label className="text-sm">
            Instrument ID
            <Input value={instrumentId} onChange={(e) => setInstrumentId(e.target.value)} placeholder="instrument uuid" />
          </label>
          <label className="text-sm">
            Account ID
            <Input value={accountId} onChange={(e) => setAccountId(e.target.value)} placeholder="broker account uuid" />
          </label>
          <Button
            variant="secondary"
            size="sm"
            disabled={!canRepair}
            onClick={() => { setActionError(null); setPendingMode('repair-netting'); }}
          >
            Repair netting
          </Button>
        </div>
      </Card>

      <RecoveryActionModal
        open={!!pendingMode}
        mode={pendingMode || 'force-close'}
        instrumentLabel={sii.trim() || 'this position'}
        submitting={submitting}
        errorMessage={actionError}
        onClose={closeModal}
        onConfirm={confirm}
      />
    </div>
  );
};

export default AdminRecoveryPanel;
